/**
 * Category ranking — wardrobe by garment category.
 */
import { store } from '../store.ts';
import { formatINR, CATEGORY_DISPLAY } from '../types.ts';
import type { ClothingCategory, CategoryStat } from '../types.ts';

export class AlmariCategoryRank extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.by_category as Partial<Record<ClothingCategory, CategoryStat>>, s.summary.total_value_inr);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(byCat: Partial<Record<ClothingCategory, CategoryStat>>, total: number): void {
    const entries = Object.entries(byCat).slice(0, 8) as [ClothingCategory, CategoryStat][];
    if (entries.length === 0) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }

    const rows = entries.map(([cat, stat], i) => {
      const pct = total > 0 ? (stat.value / total) * 100 : 0;
      return /* html */ `
<div class="row" style="--pct:${pct}%">
  <div class="rank">${String(i + 1).padStart(2, '0')}</div>
  <div class="cat">
    <div class="name">${CATEGORY_DISPLAY[cat] ?? cat}</div>
    <div class="meta">${stat.count} piece${stat.count === 1 ? '' : 's'}</div>
    <div class="meter"><span></span></div>
  </div>
  <div class="amount">${formatINR(stat.value)}</div>
</div>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 14px;
  padding-left: 4px;
}
.list { display: grid; gap: 2px; background: var(--paper-card); border: 1px solid var(--rule); border-radius: 8px; overflow: hidden; }
.row {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
}
.row:last-child { border-bottom: none; }
.rank {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--brass-dim);
  letter-spacing: 0.1em;
}
.cat { display: grid; gap: 4px; min-width: 0; }
.name {
  font-family: var(--display);
  font-size: 19px;
  color: var(--ink);
  font-weight: 700;
  letter-spacing: -0.01em;
}
.meta {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.meter {
  height: 3px;
  background: oklch(85% 0.04 60);
  border-radius: 999px;
  overflow: hidden;
}
.meter span {
  display: block;
  height: 100%;
  width: var(--pct);
  background: var(--ink);
  animation: grow 600ms var(--tx-snap) both;
}
.amount {
  font-family: var(--display);
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
  font-feature-settings: "tnum" 1;
}
@keyframes grow { from { transform: scaleX(0); transform-origin: left; } to { transform: scaleX(1); } }
</style>
<div class="title">By garment</div>
<div class="list">${rows}</div>
`;
  }
}

customElements.define('almari-category-rank', AlmariCategoryRank);
